import {
  RadioGroupBase,
  type RadioGroupBaseProps,
  type RadioOptionBase,
} from "@core/ui";
import { twMerge } from "tailwind-merge";
import { RadioGroupItem } from "./RadioGroupItem";

export type RadioOption = RadioOptionBase;

// Omit renderOption since we provide our own styled implementation
export type Props = Omit<RadioGroupBaseProps, "renderOption"> & {
  /** Layout direction - "horizontal" (default, 2 columns on md+) or "vertical" (single column) */
  direction?: "horizontal" | "vertical";
};

/**
 * Styled radio group component with card-style options.
 * Pure UI component - works with or without react-hook-form.
 *
 * @example
 * // Single select
 * <RadioGroup
 *   name="color"
 *   options={[
 *     { value: 1, label: "Red" },
 *     { value: 2, label: "Blue" },
 *   ]}
 *   value={selected}
 *   onChange={setSelected}
 * />
 *
 * @example
 * // Multi-select (up to 3)
 * <RadioGroup
 *   name="colors"
 *   options={options}
 *   value={selected}
 *   onChange={setSelected}
 *   maxItems={3}
 * />
 */
export function RadioGroup({
  name,
  options,
  value = [],
  onChange,
  onBlur,
  error,
  disabled = false,
  maxItems = 1,
  id,
  className,
  direction: _direction,
  ...props
}: Props) {
  return (
    <RadioGroupBase
      name={name}
      options={options}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={error}
      disabled={disabled}
      maxItems={maxItems}
      id={id}
      className={twMerge(
        "flex gap-4 flex-col",
        className
      )}
      renderOption={({
        option,
        isChecked,
        isDisabled,
        onChange: onOptionChange,
      }) => (
        <RadioGroupItem
          key={option.value}
          label={option.label}
          description={option.description}
          checked={isChecked}
          disabled={isDisabled}
          onChange={onOptionChange}
          className="w-full"
        />
      )}
      {...props}
    />
  );
}

export default RadioGroup;

